import { t } from '../i18n';

/**
 * First-run tutorial: a small bottom card stepping through how to use the
 * app — the steps are BUILT FROM THE ENABLED FEATURES (main.ts), so venues
 * with a minimal HUD get a minimal tour. Skippable at any point; the caller
 * persists "seen" so repeat visitors never see it again.
 */
export interface TutorialStep {
  icon: string;
  text: string;
}

export class TutorialOverlay {
  readonly el: HTMLDivElement;

  /** True while the tutorial is on screen — main.ts defers the POI picker
   *  (and anything else that would fight for the screen) until it closes. */
  get active(): boolean {
    return !this.el.classList.contains('hidden');
  }

  private steps: TutorialStep[] = [];
  private idx = 0;
  /** Receives whether the user pressed SKIP (vs finishing the steps) — per
   *  user spec (2026-07-12) the tutorial repeats EVERY session until
   *  explicitly skipped, so only skip persists "done". */
  private onDone: ((skipped: boolean) => void) | null = null;
  private iconEl: HTMLDivElement;
  private textEl: HTMLParagraphElement;
  private dotsEl: HTMLDivElement;
  private nextBtn: HTMLButtonElement;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'tutorial-card hidden';
    this.el.innerHTML = `
      <div class="tutorial-top"><span class="tutorial-title"></span><button class="btn ghost tutorial-skip"></button></div>
      <div class="tutorial-icon"></div>
      <p class="tutorial-text"></p>
      <div class="tutorial-dots"></div>
    `;
    this.iconEl = this.el.querySelector('.tutorial-icon')!;
    this.textEl = this.el.querySelector('.tutorial-text')!;
    this.dotsEl = this.el.querySelector('.tutorial-dots')!;
    this.nextBtn = document.createElement('button');
    this.nextBtn.className = 'btn primary';
    this.nextBtn.addEventListener('click', () => this.next());
    this.el.appendChild(this.nextBtn);
    (this.el.querySelector('.tutorial-skip') as HTMLButtonElement).addEventListener('click', () =>
      this.finish(true),
    );
  }

  show(steps: TutorialStep[], onDone: (skipped: boolean) => void): void {
    if (steps.length === 0) return;
    this.steps = steps;
    this.idx = 0;
    this.onDone = onDone;
    (this.el.querySelector('.tutorial-title') as HTMLElement).textContent = t('tut.title');
    (this.el.querySelector('.tutorial-skip') as HTMLButtonElement).textContent = t('tut.skip');
    this.renderStep();
    this.el.classList.remove('hidden');
  }

  private renderStep(): void {
    const step = this.steps[this.idx];
    this.iconEl.textContent = step.icon;
    this.textEl.textContent = step.text;
    this.nextBtn.textContent = this.idx === this.steps.length - 1 ? t('tut.done') : t('tut.next');
    this.dotsEl.innerHTML = this.steps
      .map((_, i) => `<span class="tutorial-dot${i === this.idx ? ' on' : ''}"></span>`)
      .join('');
  }

  private next(): void {
    if (this.idx >= this.steps.length - 1) {
      this.finish(false);
      return;
    }
    this.idx++;
    this.renderStep();
  }

  private finish(skipped: boolean): void {
    this.el.classList.add('hidden');
    const cb = this.onDone;
    this.onDone = null;
    cb?.(skipped);
  }
}
